import { Components as JoyComponents, Theme as JoyTheme } from '@mui/joy';
import JoyFormControlContext from '@mui/joy/FormControl/FormControlContext';
import JoySelect from '@mui/joy/Select';
import { mergeProps, mergeRefs, useObjectRef } from '@react-aria/utils';
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import { Description, FieldWrapper, Label } from './fieldset';
import {
  ComposedFormControllerProps,
  FormController,
  useControllerSplitProps,
  useFormController,
} from './form';

export { default as Option } from '@mui/joy/Option';

export const overrides: JoyComponents<JoyTheme>['JoySelect'] = {
  defaultProps: { size: 'md', variant: 'outlined' },
  styleOverrides: {
    root: ({ ownerState, theme }) => ({
      ...(ownerState.size === 'lg' && {
        '--Select-minHeight': theme.spacing(6),
        fontSize: theme.fontSize.md,
        lineHeight: theme.lineHeight.lg,
      }),
    }),
  },
};

export type SelectProps = React.ComponentPropsWithoutRef<typeof JoySelect> & {
  controlled?: boolean;
  /**
   * Joy Components does not play well with Radix dialogs or popovers since it
   * disabled pointer events. this prop will change the mounting position
   * of the `options` to ba attached to the root wrapper of the Select
   */
  mountOptionsToRoot?: boolean;
};

export const Select = forwardRef<HTMLDivElement, SelectProps>(function Select(
  { controlled = true, mountOptionsToRoot = false, ...props },
  ref,
) {
  const controller = useFormController();
  const objectRef = useObjectRef(ref);
  const mergedRef = mergeRefs(ref, objectRef);
  const [open, setOpen] = useState(false);
  const skipRef = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;

  const formControlContext = useContext(JoyFormControlContext);
  const labelHtmlFor = formControlContext?.htmlFor;

  const isDisabledRef = useRef(false);
  isDisabledRef.current = Boolean(
    props.disabled || formControlContext?.disabled,
  );

  const handleOpenChange = useCallback((next: boolean) => {
    if (skipRef.current) return;
    if (isDisabledRef.current) return;
    setOpen(next);
  }, []);

  useEffect(() => {
    if (!labelHtmlFor) return;
    const label = document.querySelector<HTMLLabelElement>(
      `label[for="${CSS.escape(labelHtmlFor)}"]`,
    );
    if (!label) return;
    // Skip when the click originates from an interactive descendant of the
    // label (e.g. help-icon button, popover trigger). Otherwise we'd toggle
    // the select and call preventDefault() for every nested control.
    // Bounded to descendants of the label so an interactive ancestor of the
    // label (e.g. a clickable card wrapping the form) doesn't disable us.
    const isFromInteractiveDescendant = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return false;
      const interactive = target.closest(
        'button, a, input, textarea, select, [role="button"], [role="link"], [contenteditable="true"]',
      );
      return interactive !== null && label.contains(interactive);
    };
    let wasOpenAtMouseDown = false;
    const onMouseDown = (event: Event) => {
      if (isFromInteractiveDescendant(event)) return;
      wasOpenAtMouseDown = openRef.current;
    };
    const onClick = (event: Event) => {
      if (isFromInteractiveDescendant(event)) return;
      event.preventDefault();
      handleOpenChange(!wasOpenAtMouseDown);
    };
    label.addEventListener('mousedown', onMouseDown, true);
    label.addEventListener('click', onClick);
    return () => {
      label.removeEventListener('mousedown', onMouseDown, true);
      label.removeEventListener('click', onClick);
    };
  }, [labelHtmlFor, handleOpenChange]);

  const containerRef = useRef<HTMLDivElement>(null);
  return (
    <JoySelect
      listboxOpen={open}
      onListboxOpenChange={handleOpenChange}
      data-slot='control'
      {...mergeProps(
        {
          placeholder: 'Select',
          /*
            issue: 
            options stay open when clicking outside
            ideal fix would be to update the package
            https://github.com/mui/material-ui/issues/39026

          */
          onBlur: (event) => {
            const selectElement = event.currentTarget;
            const newFocusElement =
              event.relatedTarget || document.activeElement;
            if (
              !skipRef.current &&
              !selectElement.contains(newFocusElement) &&
              !controlled
            ) {
              setOpen(false);
            }
          },
        } satisfies Partial<SelectProps>,
        props,
        controller && controlled
          ? ({
              ...controller?.field,
              onChange: (_e, value: unknown) =>
                controller?.field.onChange(value),
            } satisfies Partial<SelectProps>)
          : { ref: mergedRef },
        {
          ...(mountOptionsToRoot
            ? ({
                slotProps: {
                  root: { ref: containerRef },
                  listbox: {
                    popperOptions: { strategy: 'absolute' },
                    container: () => containerRef.current,
                  },
                },
              } satisfies Partial<SelectProps>)
            : {}),
        },
      )}
    />
  );
});

export function SelectField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  label,
  description,
  children,
  ...props
}: ComposedFormControllerProps<T, TName> &
  Omit<SelectProps, keyof ComposedFormControllerProps<T, TName>> & {
    label?: React.ReactNode;
    description?: React.ReactNode;
  }) {
  const [controllerProps, controlProps] = useControllerSplitProps(props);

  return (
    <FormController {...controllerProps}>
      <FieldWrapper>
        {label && <Label required={props.required}>{label}</Label>}
        <Select {...controlProps}>{children}</Select>
        {description && <Description>{description}</Description>}
      </FieldWrapper>
    </FormController>
  );
}
